import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTheme } from "../App";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const ResetPasswordPage = () => {
  const { token } = useParams();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? "bg-[#0A0E14]" : "bg-gray-50"}`}>
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className={`inline-flex items-center gap-1 text-sm mb-6 transition-colors ${isDark ? "text-slate-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Sign In
        </Link>

        <div className={`backdrop-blur-xl border rounded-2xl p-6 sm:p-8 ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200 shadow-sm"}`}>
          {success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className={`font-['Montserrat'] text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Password Reset!
              </h2>
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full bg-[#00677F] hover:bg-[#135163] text-white rounded-xl h-12 mt-4"
                style={{ color: "#FFFFFF" }}
                data-testid="go-to-login-btn"
              >
                Sign In
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className={`font-['Montserrat'] text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                  Set New Password
                </h2>
                <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                  Enter your new password below.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>New Password</Label>
                  <div className="relative">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      className={`pl-10 pr-10 h-12 rounded-xl ${isDark ? "bg-black/30 border-white/[0.08] text-white placeholder:text-slate-600" : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"}`}
                      data-testid="new-password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? "text-slate-500 hover:text-slate-300" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>Confirm Password</Label>
                  <div className="relative">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      className={`pl-10 h-12 rounded-xl ${isDark ? "bg-black/30 border-white/[0.08] text-white placeholder:text-slate-600" : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"}`}
                      data-testid="confirm-password-input"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="reset-error">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00677F] hover:bg-[#135163] text-white rounded-xl h-12"
                  style={{ color: "#FFFFFF" }}
                  data-testid="reset-password-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
